import {LoadingButton} from '@mui/lab';
import {Box} from '@mui/material';
import dayjs from 'dayjs';
import {useSnackbar} from 'notistack';
import {useState} from 'react';

import API, {getMessagesFromApiError} from '../../../api/axios';
import {apiBaseUrl} from '../../../api/urls';
import {AlarmHistoryListQuery} from '../../../interfaces/AlarmsHistory';
import {saveFile} from '../../../utils/file';
import {CloseSnackbarAction} from '../../common/CloseSnackbarButton';
import ExportFieldForm from '../../common/ExportFieldForm';
import ModalFixed from '../../common/ModalFixed';
import SnackbarMessages from '../../common/SnackbarMessages';

interface Props {
  alarmId?: number;
  params?: AlarmHistoryListQuery;
}

const AlarmLogTrackingReportsExportExcel = ({alarmId, params}: Props) => {
  const {enqueueSnackbar} = useSnackbar();
  const [isFetching, setIsFetching] = useState(false);
  const [isOpened, setIsOpened] = useState(false);

  const exportFieldOptions = [
    {field: 'id', label: 'ID', hidden: false},
    {field: 'type', label: 'Alarm', hidden: false},
    {field: 'reason', label: 'Reason', hidden: false},
    {field: 'reason_note', label: 'Note', hidden: false},
    {field: 'user_id', label: 'User', hidden: false},
    {field: 'message_type', label: 'Message Type', hidden: false},
    {field: 'source_mac_address', label: 'Source Mac Address', hidden: false},
    {field: 'mac_address', label: 'Mac Address', hidden: false},
    {field: 'date', label: 'Timestamp', hidden: false},
  ];

  const fetchData = async (fields: string[]) => {
    setIsOpened(false);
    setIsFetching(true);

    try {
      const reqParams = {
        export: 'excel',
        fields,
        date_start: params?.date_start,
        date_end: params?.date_end,
        order: params?.order,
        dir: params?.dir,
      };

      const resp = await API.get(
        `${apiBaseUrl}/alarm-module/${alarmId}/log`,
        {
          params: reqParams,
          responseType: 'blob',
        }
      );
      const dateStart = params?.date_start ?? dayjs().format('YYYY-MM-DD');
      const dateEnd = params?.date_end ?? dayjs().format('YYYY-MM-DD');
      saveFile(resp.data, `alarm_log_${dateStart}_${dateEnd}.xlsx`);
    } catch (error: any) {
      const messages = getMessagesFromApiError(error, 'STRA_e5f6g7h8');
      enqueueSnackbar(<SnackbarMessages messages={messages} />, {
        variant: 'error',
        action: CloseSnackbarAction,
      });
    }
    setIsFetching(false);
  };

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box display="flex" justifyContent="end">
        <LoadingButton
          fullWidth
          size="large"
          variant="contained"
          loading={isFetching}
          onClick={() => setIsOpened(true)}
        >
          Export to Excel
        </LoadingButton>
      </Box>
      <ModalFixed open={isOpened} onClose={() => setIsOpened(false)}>
        <Box
          display="flex"
          flexDirection="column"
          position="relative"
          gap={3}
          p={3}
        >
          <Box display="flex" justifyContent="space-between">
            <Box display="flex" alignItems="center" width="100%" gap={1.5}>
              <ExportFieldForm
                exportFields={exportFieldOptions}
                onChange={fetchData}
                close={() => setIsOpened(false)}
                loading={isFetching}
                columns={3}
              ></ExportFieldForm>
            </Box>
          </Box>
        </Box>
      </ModalFixed>
    </Box>
  );
};

export default AlarmLogTrackingReportsExportExcel;
